import { yerelGunBasi } from "./zaman";

/**
 * GOREV DURUM RENGI — F-STV-02a (Emre karari, 05.10.2026)
 *
 * Bu dosya SAF: veritabani, NestJS ve HTTP bilmez.
 *
 * ================================================================
 * NEDEN TEK YER
 * ================================================================
 * Ayni gorevin rengini uc ekran gosteriyor: ogrencinin "Bugun"
 * karti, ogrencinin haftalik ekrani ve kocun program ekrani. Kural her
 * ekranda ayri yazilsaydi biri "hedef alti"ni sari, digeri yesil
 * gosterirdi — ogrenci "tamamladim" sanarken koc eksik gorurdu. Sunucu
 * bu fonksiyonla hesaplayip `renk` alaninda gonderiyor; istemci yalniz
 * renge karsilik gelen belirteci seciyor.
 *
 * ================================================================
 * KURAL — UC RENK + NOTR
 * ================================================================
 *   YESIL   veri girildi ve hedefe ulasildi (hedef yoksa veri girmek yeter)
 *   SARI    veri girildi ama hedefin altinda
 *   KIRMIZI veri girilmedi ve tarihi gecti (ya da ogrenci "yapamadim" dedi)
 *   NOTR    bugun ya da gelecek, veri yok — gun bitmeden kirmizi DEGIL
 *
 * Rakip panelde "veri girdi = yesil" idi; bizde hedef alti sari, cunku
 * tamamlama orani (F-STV-02) hedef alti girisi tamamlandi saymiyor —
 * renk orandan farkli soylemesin.
 *
 * Saklanan `status` TEK BASINA YETMEZ: gecmis tarihli PLANNED/PENDING
 * gorevi FAILED'a ceviren bir zamanlanmis is yok (durum okuma aninda
 * hesaplaniyor). Renk bu yuzden veri + tarih uzerinden hesaplanir.
 *
 * ISTISNALAR (gerekceli):
 *  - Mazeret/tatil gunu (`isExcused`) NOTR: tamamlama oranina girmiyor,
 *    kirmizi gostermek ogrenciyi izinli gun icin cezalandirmak olurdu.
 *  - Ertelenen (POSTPONED) NOTR: ogrenci gerekceyle baska gune tasidi;
 *    gorevin karsiligi yeni tarihte olculur, burada ikinci kez kirmizi
 *    sayilmaz.
 *  - "Yapamadim" (FAILED) ama kismen veri girilmis: SARI. Ogrencinin
 *    yaptigi kadari kayitta (gorev.service `yapamadim` veriyi silmiyor);
 *    kirmizi, hic calismamis ogrenciyle ayni gosterirdi.
 */
export type GorevRengi = "YESIL" | "SARI" | "KIRMIZI" | "NOTR";

export const GOREV_RENKLERI: readonly GorevRengi[] = ["YESIL", "SARI", "KIRMIZI", "NOTR"];

/** Ekranda renge eslik eden metin — renk tek basina anlam tasimaz (F-STV-02). */
export const GOREV_RENGI_ETIKETI: Record<GorevRengi, string> = {
  YESIL: "Hedefe ulaştı",
  SARI: "Hedefin altında",
  KIRMIZI: "Veri girilmedi",
  NOTR: "Bekliyor",
};

export interface GorevRengiGirdisi {
  /** Turk takvim gununun UTC gece yarisi (veritabanindaki `itemDate`). */
  itemDate: Date | string;
  /** Saklanan durum — yalnizca POSTPONED ve FAILED icin okunur. */
  status: string;
  isExcused: boolean;
  targetQuestionCount: number | null;
  correctCount: number | null;
  wrongCount: number | null;
  emptyCount: number | null;
  durationSeconds: number | null;
}

export function gorevRengi(g: GorevRengiGirdisi, simdi: Date = new Date()): GorevRengi {
  if (g.isExcused) return "NOTR";
  if (g.status === "POSTPONED") return "NOTR";

  const toplam = (g.correctCount ?? 0) + (g.wrongCount ?? 0) + (g.emptyCount ?? 0);
  // Sure tek basina da veri: konu calismasi gibi sorusuz gorevlerde
  // ogrencinin girdigi tek sey sure olabilir (gorev-giris `gorevDurumu`
  // ile ayni tanim — ikisi ayrisirsa renk ile durum celisir).
  const veriVar = toplam > 0 || (g.durationSeconds ?? 0) > 0;

  if (veriVar) {
    if (g.targetQuestionCount == null) return "YESIL";
    return toplam >= g.targetQuestionCount ? "YESIL" : "SARI";
  }

  if (g.status === "FAILED") return "KIRMIZI";

  // `itemDate` ZATEN yerel gunun UTC gece yarisi; yerelGunBasi'ndan
  // GECIRILMEZ (bir gun geri kayardi — zaman.ts "AYRIM").
  const gorevGunu = new Date(g.itemDate);
  const gun = Date.UTC(gorevGunu.getUTCFullYear(), gorevGunu.getUTCMonth(), gorevGunu.getUTCDate());
  return gun < yerelGunBasi(simdi).getTime() ? "KIRMIZI" : "NOTR";
}
